import { type PresentationMode } from '@databases/modes';

import { QUERY_PARAM_PRESENTATION_MODE } from '@canvas/constants/query-params';

export const CONFLUENCE_DATABASES_BRIDGE_CLIENT_QUERY_PARAM_KEY = 'bridgeClient';

/**
 * Will avoid merging in the iframe's communicated features into your apps state.
 * Used for debugging purposes in case the other side of the iframe isn't
 * sending the correct feature set yet.
 */
export const CONFLUENCE_DATABASES_NO_MERGE_FFS_QUERY_PARAM = 'nativeDbNoMergeFfs';

export type ConfluenceDatabasesBridgeClientQueryParams = {
  /**
   * The id of the external client, sending the messages to the databases bridge client.
   */
  externalClientId: string;

  /**
   * The id of the local bridge client inside the databases embed.
   */
  localClientId: string;
};

/**
 * Parses query parameters to extract Confluence Databases bridge client configuration.
 * Returns undefined if parameters are missing, malformed, or invalid.
 */
export function maybeParseConfluenceDatabasesBridgeClientParams(queryParams: string) {
  const params = new URLSearchParams(queryParams);
  const unparsedQueryParams = params.get(
    CONFLUENCE_DATABASES_BRIDGE_CLIENT_QUERY_PARAM_KEY,
  );

  if (!unparsedQueryParams) {
    return undefined;
  }

  try {
    const parsed: ConfluenceDatabasesBridgeClientQueryParams =
      JSON.parse(unparsedQueryParams);

    if (
      typeof parsed.externalClientId === 'string' &&
      typeof parsed.localClientId === 'string'
    ) {
      return parsed;
    }
  } catch {
    return undefined;
  }
}

/**
 * Appends Confluence Databases bridge client and the Native Embed params to a database URL.
 */
export function appendConfluenceDatabasesQueryParams(
  searchParams: URLSearchParams,
  options: {
    /**
     * The configuration for the local and external bridge client IDs.
     */
    bridge: ConfluenceDatabasesBridgeClientQueryParams;

    /**
     * An optional parameter to specify the presentation mode in the query params.
     */
    presentationMode?: PresentationMode;
  },
): URLSearchParams {
  const encodedParams = JSON.stringify(options.bridge);

  searchParams.set(CONFLUENCE_DATABASES_BRIDGE_CLIENT_QUERY_PARAM_KEY, encodedParams);

  if (options.presentationMode) {
    searchParams.set(QUERY_PARAM_PRESENTATION_MODE, options.presentationMode);
  }

  return searchParams;
}

export const shouldNotMergeFeaturesBasedOnQueryParams = () =>
  new URLSearchParams(globalThis.location.search).has(
    CONFLUENCE_DATABASES_NO_MERGE_FFS_QUERY_PARAM,
  );
